import { useEffect } from "react";

import {
  initialPathUni3,
  initialPathUni4,
  REGISTERED,
  uni3BasePath,
  uni4BasePath,
  useGetLoggedInAs
} from "./profile";
import {
  PHONE_NUMBER,
  MAILING_ADDRESS,
  ADDRESS_LINE_1,
  ADDRESS_LINE_2,
  CITY,
  STATE,
  ZIPCODE,
  COUNTRY,
  LOGGED_IN_AS,
  FIRST_NAME,
  LAST_NAME
} from "../components/Profile/constants";
import clearStorageAndCookies from "./clearStorageAndCookies";
import { setPageData, dispatchEvent } from "./analytics";
import getSessionData from "./getSessionData";
import { initialPath, logout } from "./profile";
import { getCookieValue } from "./cookies";
import useUrlSlugContext from "components/UrlSlugContext";

const useLoggedInDataHandler = ({
  authData,
  setAuthData,
  profile,
  setProfile
}) => {
  const {
    trigger: getLoggedInData,
    data: loggedInData,
    isMutating: getLoggedInDataIsLoading,
    isError: getLoggedInDataError,
    error
  } = useGetLoggedInAs();
  const { urlSlug } = useUrlSlugContext();

  const { aid } = JSON.parse(getCookieValue("ww_assessment") || "{}");

  useEffect(() => {
    if (loggedInData && !getLoggedInDataError) {
      setAuthData({ ...authData, [LOGGED_IN_AS]: loggedInData?.email });
      const {
        phone,
        address,
        firstName,
        lastName,
        seqAssessmentId,
        isB2B,
        isIAP,
        isLapsed,
        isRegistered
      } = loggedInData || {};
      const { streetAddress, locality, region, postalCode, country } =
        address || {};
      const hasAddressData = !!(
        streetAddress &&
        locality &&
        region &&
        postalCode &&
        country
      );
      const {
        phoneNumber: existingPhone,
        mailingAddress,
        firstName: existingFirstName,
        lastName: existingLastName
      } = profile || {};
      const {
        [ADDRESS_LINE_1]: existingStreetAddress,
        [CITY]: existingLocality,
        [STATE]: existingRegion,
        [ZIPCODE]: existingPostalCode
      } = mailingAddress || {};
      const hasExistingProfileData = !!(
        existingStreetAddress &&
        existingLocality &&
        existingRegion &&
        existingPostalCode &&
        existingPhone
      );
      const namesData = {
        [FIRST_NAME]: existingFirstName || firstName,
        [LAST_NAME]: existingLastName || lastName
      };
      if (profile && setProfile && hasAddressData && !hasExistingProfileData) {
        const phoneNumber = (phone || "").replace(/[^0-9]/g, "");
        setProfile({
          ...profile,
          [PHONE_NUMBER]: phoneNumber,
          [MAILING_ADDRESS]: {
            [ADDRESS_LINE_1]: streetAddress,
            [ADDRESS_LINE_2]: null,
            [CITY]: locality,
            [STATE]: region,
            [ZIPCODE]: postalCode,
            [COUNTRY]: country
          },
          ...namesData,
          seqAssessmentId,
          isB2B,
          isIAP,
          isLapsed,
          isRegistered
        });
      } else if (profile && setProfile) {
        setProfile({
          ...profile,
          ...namesData,
          // Properties below are undefined for users who have just signed up.
          seqAssessmentId,
          isB2B,
          isIAP,
          isLapsed,
          isRegistered
        });
      }
      const { uuid } = getSessionData();
      setPageData({
        site_section: "clinical",
        user_uuid: uuid,
        user_email: loggedInData?.email
      });
      dispatchEvent({
        event: "non_click_event",
        event_key: "clinical_user_identified",
        event_category: "quiz",
        event_action: "clinical_user_identified"
      });

      // Used for email campaigns.
      if (uuid) {
        dispatchEvent({
          event: "non_click_event",
          event_key: "assessment_user_identified",
          event_category: "assessment",
          event_action: "assessment_user_identified",
          user_id: uuid,
          assessment_type: urlSlug
        });
      }

      // If the user has hit the login button.
      const attemptedLogin = window.sessionStorage.getItem("attemptedLogin");
      if (attemptedLogin) {
        window.sessionStorage.removeItem("attemptedLogin");

        // Fire events based on the user's entitlements.
        if (loggedInData?.entitlements?.includes(REGISTERED)) {
          dispatchEvent({
            event: "non_click_event",
            event_key: "quiz:logged_in_registered",
            event_category: "quiz",
            event_action: "logged_in_registered"
          });
        } else {
          dispatchEvent({
            event: "non_click_event",
            event_key: "quiz:logged_in_active",
            event_category: "quiz",
            event_action: "logged_in_active"
          });
        }
      }
    } else if (error?.status === 401) {
      setAuthData({ ...authData, [LOGGED_IN_AS]: "" });
      if (!aid) {
        clearStorageAndCookies();
        let redirectPath = initialPath;
        if (window.location.pathname.startsWith(uni3BasePath)) {
          redirectPath = initialPathUni3;
        }
        if (window.location.pathname.startsWith(uni4BasePath)) {
          redirectPath = initialPathUni4;
        }
        logout(`${window.location.origin}${redirectPath}`);
      }
    }
  }, [loggedInData, getLoggedInDataError]);

  return {
    getLoggedInData,
    loggedInData,
    getLoggedInDataIsLoading,
    getLoggedInDataError
  };
};

export default useLoggedInDataHandler;
